import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { LANGUAGE_OPTIONS } from "../../features/localization/languages";

interface LanguageMenuProps {
  /** The two-letter code of the language on screen. */
  value: string;
  onSelect: (code: string) => void;
}

/**
 * The portal header's language choice (US-96): a globe and the current code (`DE`) opening a small
 * menu of the full names, so the header keeps its room on a phone. Arrow keys move, Escape closes,
 * and focus returns to the trigger.
 */
const LanguageMenu = ({ value, onSelect }: LanguageMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const current = LANGUAGE_OPTIONS.find((language) => language.code === value) ?? LANGUAGE_OPTIONS[0];
  const currentIndex = LANGUAGE_OPTIONS.indexOf(current);

  const close = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    itemRefs.current[currentIndex]?.focus();
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, currentIndex]);

  const focusItem = (index: number): void => {
    const count = LANGUAGE_OPTIONS.length;
    itemRefs.current[(index + count) % count]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const focused = itemRefs.current.findIndex((item) => item === document.activeElement);
    const moves: Record<string, number> = {
      ArrowDown: focused + 1,
      ArrowUp: focused - 1,
      Home: 0,
      End: LANGUAGE_OPTIONS.length - 1,
    };
    if (event.key in moves) {
      event.preventDefault();
      focusItem(moves[event.key]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const pick = (code: string): void => {
    close();
    if (code !== value) onSelect(code);
  };

  return (
    <div ref={rootRef} className="relative" data-testid="language-menu">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t("portal.language", "Language")}: ${current.label}`}
        onClick={() => setOpen((isOpen) => !isOpen)}
        onKeyDown={onTriggerKeyDown}
        className="flex min-h-[44px] items-center gap-1 rounded-md px-2 text-sm font-medium text-grey-20 hover:bg-grey-95 dark:text-white dark:hover:bg-primary-20"
      >
        <Globe aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span>{current.code.toUpperCase()}</span>
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t("portal.language", "Language")}
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full z-50 mt-2 w-48 rounded-md border border-grey-90 bg-white py-1 shadow-lg dark:border-grey-30 dark:bg-primary-10"
        >
          {LANGUAGE_OPTIONS.map((language, index) => (
            <button
              key={language.code}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={language.code === current.code}
              lang={language.code}
              tabIndex={-1}
              onClick={() => pick(language.code)}
              className={`block w-full px-4 py-2 text-left text-sm text-grey-20 hover:bg-grey-95 dark:text-white dark:hover:bg-primary-20 ${
                language.code === current.code ? "font-medium" : ""
              }`}
            >
              {language.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageMenu;
